import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { KmToMilesPipe } from 'src/app/_shared/pipes/km-to-miles.pipe';
import { createPlotlyChart, deletePlotlyChart } from 'src/app/_shared/plotlyConfig';

@Component({
  selector: 'app-sohc-over-mileage-chart',
  templateUrl: './sohc-over-mileage-chart.component.html',
  styleUrls: ['./sohc-over-mileage-chart.component.scss']
})
export class SohcOverMileageChartComponent implements OnChanges {
  @Input() filteredVehiclesData!: any[];
  @Input() selectedVehicles!: any[];
  @Input() companyData: any;
  @Input() dataLoadingError: boolean = false;
  @Input() isMetric: boolean | undefined;
  private readonly kmToMilesPipe = new KmToMilesPipe();
  deviceSerialNumber: string[] = [];
  private colorMap: { [key: string]: string } = {};
  xRange: [number, number] | undefined = undefined;
  @Input() sohTrajectories: any;
  @Input() errorMessageSohTrajectories: boolean = false;
  @Input() dataLoadingSohTrajectories: boolean = false;

  constructor() { }

  ngOnChanges(changes: SimpleChanges): void {
    this.dataLoadingSohTrajectories = true;
    this.errorMessageSohTrajectories = false;
    const currentSelectedVehicles = changes['selectedVehicles']?.currentValue ?? this.selectedVehicles;
    const sohChanged = !!changes['sohTrajectories'];

    if (Array.isArray(currentSelectedVehicles) && currentSelectedVehicles.length > 0 && sohChanged) {
      if (!this.sohTrajectories || Object.keys(this.sohTrajectories).length === 0) {
        this.dataLoadingSohTrajectories = false;
        this.errorMessageSohTrajectories = true;
        deletePlotlyChart('sohc-over-mileage-chart');
        return;
      }

      this.dataLoadingSohTrajectories = true;
      this.errorMessageSohTrajectories = false;
      this.deviceSerialNumber = this.selectedVehicles.map(vehicle => vehicle.batteryIdentifier);

      this.initializeColorMap();
      const allPlotlyData: any[] = [];
      const shapes: any[] = [];
      const annotations: any[] = [];

      // Global variables for the X range
      let globalEstimationEnd: number | null = null;
      let globalPredictionEnd: number | null = null;

      Object.keys(this.sohTrajectories).forEach((deviceSerialNumberId: string) => {
        const vehicle = this.selectedVehicles.find(vehicle => vehicle.batteryIdentifier === deviceSerialNumberId);
        const deviceData = this.sohTrajectories[deviceSerialNumberId];
        const estimationData = this.sortByMileage(deviceData.estimations || []);
        const predictionData = this.sortByMileage(deviceData.predictions || []);
        // Global variables for the X range calculation
        if (estimationData.length > 0) {
          const lastEstimation = estimationData[estimationData.length - 1];
          const lastEstimationX = this.getConvertedMileageValue(lastEstimation.mileage);
          if (!globalEstimationEnd || lastEstimationX > globalEstimationEnd) {
            globalEstimationEnd = lastEstimationX;
          }
        }
        if (predictionData.length > 0) {
          const lastPrediction = predictionData[predictionData.length - 1];
          const lastPredictionX = this.getConvertedMileageValue(lastPrediction.mileage);
          if (!globalPredictionEnd || lastPredictionX > globalPredictionEnd) {
            globalPredictionEnd = lastPredictionX;
          }
        }

        // Trace estimation (only if there is data)
        if (estimationData.length > 0) {
          allPlotlyData.push({
            x: estimationData.map((point: any) => this.getConvertedMileageValue(point.mileage)),
            y: estimationData.map((point: any) => point.batteryStateOfHealth),
            type: 'scatter',
            mode: 'lines',
            name: vehicle.name + ' Estimation',
            line: { dash: 'solid' },
            marker: { color: this.colorMap[deviceSerialNumberId] },
            showlegend: true
          });

          // Marker for last estimation
          const lastEstimation = estimationData[estimationData.length - 1];
          const lastEstimationX = this.getConvertedMileageValue(lastEstimation.mileage);
          const lastEstimationY = lastEstimation.batteryStateOfHealth;

          allPlotlyData.push({
            x: [lastEstimationX],
            y: [lastEstimationY],
            type: 'scatter',
            mode: 'markers+text',
            name: vehicle.name + ' Last Estimation',
            marker: {
              color: this.colorMap[deviceSerialNumberId],
              size: 16,
              symbol: 'circle'
            },
            showlegend: false
          });

          // Vertical dashed line for last estimation
          shapes.push({
            type: 'line',
            xref: 'x',
            yref: 'y',
            x0: lastEstimationX,
            y0: 0,
            x1: lastEstimationX,
            y1: lastEstimationY,
            line: {
              dash: 'dot',
              color: this.colorMap[deviceSerialNumberId],
              width: 1
            }
          });
        }

        // Trace prediction & marker (only if there is predictionData)
        if (predictionData.length > 0) {
          const lastPrediction = predictionData[predictionData.length - 1];
          const lastPredictionX = this.getConvertedMileageValue(lastPrediction.mileage);
          const lastPredictionY = lastPrediction.batteryStateOfHealth;

          // Arrow marker only if there are at least 2 predictions
          if (predictionData.length > 1) {
            const beforeLastPrediction = predictionData[predictionData.length - 2];
            const beforeLastPredictionX = this.getConvertedMileageValue(beforeLastPrediction.mileage);
            const beforeLastPredictionY = beforeLastPrediction.batteryStateOfHealth;

            allPlotlyData.push({
              x: [beforeLastPredictionX],
              y: [beforeLastPredictionY],
              type: 'scatter',
              mode: 'markers',
              marker: {
                color: this.colorMap[deviceSerialNumberId],
                size: 8,
                symbol: 'arrow-right'
              },
              showlegend: false
            });
          }

          // Marker for last prediction
          allPlotlyData.push({
            x: [lastPredictionX],
            y: [lastPredictionY],
            type: 'scatter',
            mode: 'markers',
            name: vehicle.name + ' Prediction',
            marker: {
              color: this.colorMap[deviceSerialNumberId],
              size: 16,
              symbol: 'circle-open',
              line: { width: 4 }
            },
            showlegend: false
          });

          // Horizontal dashed line from last estimation to last prediction
          if (estimationData.length > 0) {
            const lastEstimation = estimationData[estimationData.length - 1];
            const lastEstimationX = this.getConvertedMileageValue(lastEstimation.mileage);
            shapes.push({
              type: 'line',
              xref: 'x',
              yref: 'y',
              x0: lastEstimationX,
              y0: lastPredictionY,
              x1: lastPredictionX,
              y1: lastPredictionY,
              line: {
                dash: 'dot',
                color: this.colorMap[deviceSerialNumberId],
                width: 1
              }
            });
          }
        }
      });

      // Legend entries for prediction and estimation
      const legendPredictionDot = {
        x: [null],
        y: [null],
        type: 'scatter',
        mode: 'markers',
        name: 'Last Estimation',
        marker: {
          size: 16,
          symbol: 'dot',
          color: 'black'
        },
        showlegend: true
      };

      const legendEstimationDot = {
        x: [null],
        y: [null],
        type: 'scatter',
        mode: 'markers',
        name: 'Prediction', /* One Year Prediction */
        marker: {
          size: 16,
          symbol: 'circle-open',
          color: 'black',
          line: { width: 4 }
        },
        showlegend: true
      };

      allPlotlyData.push(legendPredictionDot, legendEstimationDot);

      // Calculating the global X range
      const minusTenThousand = 10000;
      const plusFiveThousand = 5000;
      if (globalEstimationEnd && globalPredictionEnd) {
        const xMin = globalEstimationEnd - minusTenThousand;
        const xMax = globalPredictionEnd + plusFiveThousand;
        this.xRange = [xMin, xMax];
      } else if (globalEstimationEnd) {
        const xMin = globalEstimationEnd - minusTenThousand;
        const xMax = globalEstimationEnd + plusFiveThousand;
        this.xRange = [xMin, xMax];
      } else if (globalPredictionEnd) {
        const xMin = globalPredictionEnd - minusTenThousand;
        const xMax = globalPredictionEnd + plusFiveThousand;
        this.xRange = [xMin, xMax];
      } else {
        this.xRange = undefined;
      }

      const yRange = this.getYRange(allPlotlyData);

      const layout = {
        title: 'State of Health over mileage',
        font: {
          family: 'boschsans, Helvetica Neue, Helvetica, Arial, sans-serif',
          size: 13,
        },
        xaxis: { 
          title: `Mileage [${this.getMileageAxisLabel()}]`,
          /* range: this.selectedVehicles.length == 1 ? this.xRange : undefined */
        },
        yaxis: { 
          title: 'State of Health [%]',
          range: yRange
        },
        showlegend: true,
        legend: {
          orientation: 'h',
          y: -0.3,
          xanchor: 'center',
          x: 0.5,
          font: {
            family: 'boschsans, Helvetica Neue, Helvetica, Arial, sans-serif',
            size: 13,
          },
          itemclick: false,
          itemdoubleclick: false,
        },
        shapes: shapes,
        annotations: annotations
      };

      createPlotlyChart('sohc-over-mileage-chart', allPlotlyData, layout);
      this.dataLoadingSohTrajectories = false;
      
    } else if (this.dataLoadingError) {
      this.dataLoadingSohTrajectories = false;
      this.errorMessageSohTrajectories = true;
    }
  }

  sortByMileage(data: any[]): any[] {
    return data.slice().sort((a, b) => a.mileage - b.mileage);
  }


  initializeColorMap(): void {
    const colors = ['#007BC0', '#18837E','#9E2896'];
    this.selectedVehicles.forEach((vehicle, index) => {
      this.colorMap[vehicle.batteryIdentifier] = colors[index % colors.length];
    });
  }

  getConvertedMileageValue(val: number): number {
    if (this.isMetric !== null && this.isMetric !== undefined) {
      return this.isMetric ? val : this.kmToMilesPipe.transform(val);
    } else {
      return this.companyData?.companyAddress?.country === 'United States (US)'
        ? this.kmToMilesPipe.transform(val)
        : val;
    }
  }

  getMileageAxisLabel(): string {
    if (this.isMetric !== null && this.isMetric !== undefined) {
      return this.isMetric ? 'km' : 'mi';
    } else {
      return this.companyData?.companyAddress?.country === 'United States (US)'
        ? 'mi'
        : 'km';
    }
  }

  private getYRange(allPlotlyData: any[]): [number, number] {
    let min = Number.POSITIVE_INFINITY;
    let max = Number.NEGATIVE_INFINITY;
    allPlotlyData.forEach(trace => {
      if (Array.isArray(trace.y)) {
        trace.y.forEach((val: number) => {
          if (typeof val === 'number' && !isNaN(val)) {
            min = Math.min(min, val);
            max = Math.max(max, val);
          }
        });
      }
    });
    // Default range if no data
    if (!isFinite(min) || !isFinite(max)) {
      min = 80;
      max = 100;
    }
    return [min - 5, max + 5];
  }
}
